import React from 'react';
import ScrollAnimation from './ScrollAnimation';
import { useGradientAnimation } from '@/hooks/useGradientAnimation';
import { motion } from 'framer-motion';
import '@/styles/steps.css';

const steps = [
  {
    iconUrl: "https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/S1.svg?alt=media&token=644f8bad-bf81-4174-a36e-02a30f082b8b",
    title: '1. Select Your Growth Objective',
    description: 'Choose the specific area you want to focus on for your professional development'
  },
  {
    iconUrl: "https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/S2.svg?alt=media&token=2571089a-d26d-4663-af62-aa676e391a90",
    title: '2. Complete Self-Assessment',
    description: 'Reflect on your skills and competencies through our structured assessment'
  },
  {
    iconUrl: "https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/s3.svg?alt=media&token=980eb830-7b56-4c50-bca5-550c4199a562",
    title: '3. Nominate Evaluators and provide context',
    description: 'Select 5-10 evaluators to provide feedback, and provide context your relation'
  },
  {
    iconUrl: "https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/s4.svg?alt=media&token=c133b0b0-5054-4037-aa5f-668f41cf679b",
    title: '4. Receive A Comprehensive Report',
    description: 'Get detailed insights and actionable recommendations for your career growth'
  }
];

export default function HowItWorks() {
  const gradientStyle = useGradientAnimation();

  return (
    <ScrollAnimation>
      <section id="how-it-works" className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 w-full h-full"
          style={gradientStyle}
        />
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-block relative mb-4"
            >
              <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 text-primary px-6 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border border-primary/10">
                Simple Process
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur-xl opacity-50 -z-10" />
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Evolve Your Career in <span className="text-white">4 Simple Steps</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Our straightforward process helps you gather meaningful insights for your professional growth
            </p>
          </div>

          <div className="relative steps-container">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white/20 hidden lg:block step-line" />
            <div className="absolute left-8 sm:left-12 top-0 bottom-0 w-0.5 bg-white/20 lg:hidden step-line" />

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`relative flex items-start mb-12 last:mb-0 lg:mb-16 step-item ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                <div className="relative flex-shrink-0 z-10">
                  <div className="w-16 h-16 sm:w-24 sm:h-24 ml-0 lg:ml-0 relative">
                    <motion.div 
                      initial={{ height: 0 }}
                      whileInView={{ height: '100%' }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      viewport={{ once: true }}
                      className="absolute left-8 sm:left-12 top-16 sm:top-24 w-0.5 bg-white/20 lg:hidden step-line"
                      style={{ height: index === steps.length - 1 ? 0 : '100px' }}
                    />
                    
                    <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gradient-to-r from-[#4cdcdd] to-[#ef9aeb] p-0.5 transform lg:scale-125">
                      <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                        <img 
                          src={step.iconUrl}
                          alt={`Step ${index + 1}`}
                          className="w-8 h-8 sm:w-10 sm:h-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`flex-1 ml-6 sm:ml-8 lg:ml-0 ${
                  index % 2 === 0 ? 'lg:pr-20 lg:text-right' : 'lg:pl-20'
                }`}>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    viewport={{ once: true }}
                    className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 step-card step-animation"
                  >
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </motion.div>
                </div>

                <div className="hidden lg:block flex-1" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </ScrollAnimation>
  );
}